import { useState, useEffect, useContext, useCallback } from "react";
import { Row, Col, Select, Button, Spin, Modal, Avatar, Tag, Empty, Input, Space, message } from "antd";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import {
  FileProtectOutlined, BarChartOutlined, TeamOutlined,
  MoneyCollectOutlined, BranchesOutlined, CalendarOutlined,
  SafetyCertificateOutlined, ReloadOutlined,
  CheckCircleOutlined, ClockCircleOutlined, UsergroupAddOutlined,
  ManOutlined, WomanOutlined, ScheduleOutlined,
  UserOutlined, CheckOutlined, FileTextOutlined, FieldTimeOutlined,
  SearchOutlined, PrinterOutlined, CopyOutlined, ThunderboltOutlined,
  FileDoneOutlined
} from "@ant-design/icons";

import { useNavigation } from "../../context/NavigationContext";
import TotalEmployees from "../../components/HrDashboard/TotalEmployees";
import AttendanceReport from "../../components/HrDashboard/AttendanceReport";
import LeaveUtilization from "../../components/HrDashboard/LeaveUtilization";
import OrganisationLeave from "../../components/HrDashboard/OrganisationLeave";
import PayrollSummary from "../../components/HrDashboard/PayrollSummary";
import DepartmentWiseSalary from "../../components/HrDashboard/DepartmentWiseSalary";
import StatutoryCompliance from "../../components/HrDashboard/StatutoryCompliance";
import HrAnalytics from "../../components/HrDashboard/HrAnalytics";
import EmployeeAttendance from "../../components/HrDashboard/EmployeeAttendance";
import OtChart from "../../components/HrDashboard/OtChart";
import LopChart from "../../components/HrDashboard/LopChart";
import DepartmentAttendanceBenchmark from "../../components/HrDashboard/DepartmentAttendanceBenchmark";
import OrgChartList from "./OrgChartList";
import dayjs from "dayjs";

const { Option } = Select;
const PRIMARY = "#0e497b";

/* ==============================================================
   KPI CARD — Executive Luxury Clickable Version
============================================================== */
function KpiCard({
  accent, icon, label, bigValue, loading,
  s1Val, s1Label, s1Color, s1Icon, onS1Click,
  s2Val, s2Label, s2Color, s2Icon, onS2Click,
  s3Val, s3Label, s3Color, s3Icon, onS3Click,
  extraTag, onExtraTagClick,
  onCardClick,
}) {
  const stats = [
    { val: s1Val, label: s1Label, color: s1Color, icon: s1Icon, onClick: onS1Click },
    { val: s2Val, label: s2Label, color: s2Color, icon: s2Icon, onClick: onS2Click },
    { val: s3Val, label: s3Label, color: s3Color, icon: s3Icon, onClick: onS3Click },
  ].filter(s => s.label);

  return (
    <div
      onClick={onCardClick}
      style={{
        background: `linear-gradient(145deg, #ffffff 0%, #f8fafc 65%, ${accent}0d 100%)`,
        borderRadius: 18,
        border: `1.5px solid ${accent}28`,
        boxShadow: `0 8px 24px -4px rgba(15, 23, 42, 0.06), 0 2px 6px -1px rgba(15, 23, 42, 0.04)`,
        padding: "18px 20px 16px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        height: "100%",
        transition: "all 0.28s cubic-bezier(0.4, 0, 0.2, 1)",
        position: "relative",
        overflow: "hidden",
        cursor: onCardClick ? "pointer" : "default",
      }}
      onMouseEnter={e => {
        e.currentTarget.style.boxShadow = `0 16px 36px -6px ${accent}28, 0 6px 16px -2px rgba(15, 23, 42, 0.08)`;
        e.currentTarget.style.transform = "translateY(-4px)";
        e.currentTarget.style.borderColor = `${accent}66`;
      }}
      onMouseLeave={e => {
        e.currentTarget.style.boxShadow = `0 8px 24px -4px rgba(15, 23, 42, 0.06), 0 2px 6px -1px rgba(15, 23, 42, 0.04)`;
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.borderColor = `${accent}28`;
      }}
    >
      {/* Top Multi-Color Shimmer Line */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0,
        height: 4, background: `linear-gradient(90deg, ${accent}, ${accent}bb, #ffffff, ${accent}88)`,
      }} />

      {/* Top Hero Section */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginTop: 2, marginBottom: 14 }}>
        <div>
          {/* Label Pill */}
          <span style={{
            fontSize: 10.5, fontWeight: 800, color: accent,
            textTransform: "uppercase", letterSpacing: "0.08em",
            background: `${accent}14`, border: `1px solid ${accent}30`,
            padding: "3px 11px", borderRadius: 12,
            display: "inline-block", marginBottom: 10,
            boxShadow: `0 2px 6px ${accent}12`,
          }}>
            {label}
          </span>
          {/* Big Hero Number & extraTag */}
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
            <div style={{
              fontSize: 36, fontWeight: 900, color: accent,
              lineHeight: 1, letterSpacing: "-0.04em",
              fontFeatureSettings: '"cv02", "cv03", "cv04", "cv11"',
            }}>
              {loading ? <Spin size="small" /> : bigValue}
            </div>
            {extraTag && !loading && (
              <span
                onClick={e => {
                  if (onExtraTagClick) {
                    e.stopPropagation();
                    onExtraTagClick();
                  }
                }}
                style={{
                  fontSize: 11, fontWeight: 700, color: "#334155",
                  background: "#f1f5f9", border: "1px solid #cbd5e1",
                  padding: "3px 10px", borderRadius: 12, whiteSpace: "nowrap",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                  cursor: onExtraTagClick ? "pointer" : "default"
                }}
              >
                {extraTag}
              </span>
            )}
          </div>
        </div>

        {/* 3D Floating Glowing Icon Container */}
        <div style={{
          width: 48, height: 48, borderRadius: 15,
          background: `linear-gradient(135deg, ${accent} 0%, ${accent}ee 100%)`,
          color: "#ffffff", fontSize: 22, flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: `0 8px 20px ${accent}45`,
          border: "1.5px solid rgba(255,255,255,0.3)"
        }}>
          {icon}
        </div>
      </div>

      {/* Bottom Sub-stats Grid */}
      {stats.length > 0 && (
        <div style={{
          display: "grid",
          gridTemplateColumns: `repeat(${stats.length}, 1fr)`,
          gap: 8,
          paddingTop: 12,
          borderTop: `1px dashed ${accent}25`,
        }}>
          {stats.map((st, i) => (
            <div
              key={i}
              onClick={e => { e.stopPropagation(); st.onClick && st.onClick(); }}
              style={{
                background: `${st.color}10`,
                border: `1px solid ${st.color}30`,
                borderRadius: 12,
                padding: "8px 10px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
                minWidth: 0,
                cursor: st.onClick ? "pointer" : "default",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
              onMouseEnter={e => {
                if (st.onClick) {
                  e.currentTarget.style.background = `${st.color}22`;
                  e.currentTarget.style.borderColor = `${st.color}55`;
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = `0 4px 12px ${st.color}25`;
                }
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = `${st.color}10`;
                e.currentTarget.style.borderColor = `${st.color}30`;
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "0 1px 3px rgba(0,0,0,0.03)";
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 5, minWidth: 0, overflow: "hidden" }}>
                {st.icon && <span style={{ color: st.color, fontSize: 13, display: "flex", alignItems: "center", flexShrink: 0 }}>{st.icon}</span>}
                <span style={{ fontSize: 11, fontWeight: 700, color: st.color, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{st.label}</span>
              </div>
              <span style={{ fontSize: 14, fontWeight: 900, color: st.color, flexShrink: 0, marginLeft: 4 }}>
                {loading ? "-" : st.val}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ==============================================================
   ATTENDANCE MODAL — employee list with live search bar
============================================================== */
function AttendanceModal({ open, onClose, title, statusColor, employees, loading }) {
  const [searchText, setSearchText] = useState("");

  useEffect(() => {
    if (!open) {
      setSearchText("");
    }
  }, [open]);

  const filteredEmployees = (employees || []).filter(emp => {
    if (!searchText.trim()) return true;
    const q = searchText.trim().toLowerCase();
    const name = (emp.employee_name || "").toLowerCase();
    const code = (emp.emp_code || "").toLowerCase();
    const dept = (emp.department || "").toLowerCase();
    const desig = (emp.designation || "").toLowerCase();
    return name.includes(q) || code.includes(q) || dept.includes(q) || desig.includes(q);
  });

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={720}
      bodyStyle={{ padding: "16px 24px 24px" }}
      title={
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingRight: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 12, height: 12, borderRadius: "50%",
              background: statusColor || PRIMARY, flexShrink: 0,
              boxShadow: `0 0 8px ${statusColor || PRIMARY}66`
            }} />
            <span style={{ fontWeight: 800, fontSize: 16, color: "#0f172a" }}>{title}</span>
          </div>
          {!loading && employees && (
            <Tag color="blue" style={{ borderRadius: 12, padding: "2px 10px", fontWeight: 700, fontSize: 12 }}>
              {filteredEmployees.length} of {employees.length}
            </Tag>
          )}
        </div>
      }
    >
      {/* Search Input Bar */}
      <div style={{ marginBottom: 14, marginTop: 4 }}>
        <Input
          placeholder="Search by Employee Name, Code, Department, Designation..."
          prefix={<SearchOutlined style={{ color: "#94a3b8", fontSize: 15 }} />}
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          allowClear
          size="large"
          style={{
            borderRadius: 10,
            border: "1px solid #cbd5e1",
            boxShadow: "0 1px 4px rgba(15,23,42,0.04)",
            fontSize: 13
          }}
        />
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: "50px 0" }}>
          <Spin size="large" />
          <div style={{ marginTop: 12, fontSize: 12, color: "#64748b", fontWeight: 600 }}>Loading employee records...</div>
        </div>
      ) : !employees || employees.length === 0 ? (
        <Empty description="No employee records found for today" style={{ padding: "36px 0" }} />
      ) : filteredEmployees.length === 0 ? (
        <Empty description={`No matching employee for "${searchText}"`} style={{ padding: "36px 0" }} />
      ) : (
        <div style={{ maxHeight: 460, overflowY: "auto", paddingRight: 4 }}>
          {filteredEmployees.map((emp, idx) => (
            <div
              key={emp.employee_id || idx}
              style={{
                display: "flex", alignItems: "center", gap: 14,
                padding: "11px 14px",
                borderRadius: 12,
                border: "1px solid #f1f5f9",
                marginBottom: 8,
                background: idx % 2 === 0 ? "#f8fafc" : "#ffffff",
                transition: "all 0.18s ease-in-out",
                boxShadow: "0 1px 3px rgba(15,23,42,0.02)"
              }}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = `${PRIMARY}30`;
                e.currentTarget.style.background = "#eff6ff";
                e.currentTarget.style.transform = "translateX(2px)";
              }}
              onMouseLeave={e => {
                e.currentTarget.style.borderColor = "#f1f5f9";
                e.currentTarget.style.background = idx % 2 === 0 ? "#f8fafc" : "#ffffff";
                e.currentTarget.style.transform = "translateX(0)";
              }}
            >
              <Avatar
                src={emp.photo}
                icon={<UserOutlined />}
                size={44}
                style={{
                  flexShrink: 0,
                  background: `linear-gradient(135deg, ${PRIMARY}20, ${PRIMARY}40)`,
                  border: "1.5px solid #e2e8f0"
                }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontWeight: 800, fontSize: 13.5, color: "#0f172a" }}>
                    {emp.employee_name}
                  </span>
                  <Tag style={{
                    fontSize: 10, fontWeight: 700, borderRadius: 6,
                    background: "#e2e8f0", border: "none", color: "#334155",
                    padding: "0 6px"
                  }}>
                    {emp.emp_code}
                  </Tag>
                </div>
                <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 2 }}>
                  {emp.department} · {emp.designation}
                </div>
              </div>
              <div style={{ textAlign: "right", flexShrink: 0 }}>
                {(() => {
                  const tagText = emp.sub_info || (emp.check_in && emp.check_in !== "-" ? (
                    typeof emp.check_in === "string" && (emp.check_in.includes("AM") || emp.check_in.includes("PM"))
                      ? `In: ${emp.check_in}`
                      : (dayjs(emp.check_in).isValid() ? `In: ${dayjs(emp.check_in).format("hh:mm A")}` : `In: ${emp.check_in}`)
                  ) : null);

                  if (!tagText) return null;

                  return (
                    <Tag
                      color={
                        statusColor === "#15803d" || statusColor === "#059669" ? "green" :
                        (statusColor === "#b45309" || statusColor === "#d97706" ? "warning" :
                        (statusColor === "#dc2626" || statusColor === "#991b1b" ? "red" : "blue"))
                      }
                      style={{ borderRadius: 8, padding: "3px 10px", fontWeight: 700, fontSize: 11.5 }}
                    >
                      {tagText}
                    </Tag>
                  );
                })()}
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

/* ==============================================================
   QUICK ACTION BUTTON — with pending count badge support
============================================================== */
function QuickBtn({ icon, label, color, count, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "inline-flex", alignItems: "center", gap: 8,
        padding: "8px 16px", borderRadius: 11,
        background: "#ffffff", border: `1.5px solid ${color}35`,
        color: "#1e293b", fontWeight: 700, fontSize: 12.5,
        cursor: "pointer", outline: "none",
        transition: "all 0.22s cubic-bezier(0.4, 0, 0.2, 1)", whiteSpace: "nowrap",
        boxShadow: "0 2px 6px rgba(15,23,42,0.04)",
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = color;
        e.currentTarget.style.color = color;
        e.currentTarget.style.background = `${color}12`;
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow = `0 6px 16px ${color}25`;
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = `${color}35`;
        e.currentTarget.style.color = "#1e293b";
        e.currentTarget.style.background = "#ffffff";
        e.currentTarget.style.transform = "translateY(0)";
        e.currentTarget.style.boxShadow = "0 2px 6px rgba(15,23,42,0.04)";
      }}
    >
      <span style={{ fontSize: 15, color: color, display: "flex", alignItems: "center" }}>{icon}</span>
      <span>{label}</span>
      {count > 0 && (
        <span style={{
          background: color, color: "#ffffff",
          fontSize: 11, fontWeight: 900,
          padding: "2px 8px", borderRadius: 12,
          marginLeft: 2, boxShadow: `0 2px 8px ${color}50`
        }}>
          {count}
        </span>
      )}
    </button>
  );
}

/* ==============================================================
   SECTION HEADER
============================================================== */
function SecHead({ icon, title, subtitle }) {
  return (
    <div style={{ marginBottom: 18, paddingBottom: 12, borderBottom: "1.5px dashed #cbd5e1" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{
          width: 34, height: 34, borderRadius: 10,
          background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)", color: PRIMARY,
          display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0,
          border: "1px solid #bfdbfe", boxShadow: "0 2px 6px rgba(14, 73, 123, 0.1)",
        }}>
          {icon}
        </div>
        <div>
          <div style={{ fontWeight: 800, fontSize: 15, color: "#0f172a", letterSpacing: "-0.01em" }}>{title}</div>
          {subtitle && <div style={{ fontSize: 12, color: "#64748b", marginTop: 2, fontWeight: 500 }}>{subtitle}</div>}
        </div>
      </div>
    </div>
  );
}

/* ==============================================================
   MODULE TAB BAR — Luxury Glassmorphic Tabs
============================================================== */
const TAB_DEFS = [
  { key: "attendance", label: "Attendance & Workforce", icon: <CalendarOutlined />, color: "#0e497b", gradient: "linear-gradient(135deg, #0e497b 0%, #1d4ed8 100%)" },
  { key: "payroll", label: "Payroll & Salary", icon: <MoneyCollectOutlined />, color: "#059669", gradient: "linear-gradient(135deg, #059669 0%, #10b981 100%)" },
  { key: "statutory", label: "Statutory Compliance", icon: <SafetyCertificateOutlined />, color: "#d97706", gradient: "linear-gradient(135deg, #d97706 0%, #f59e0b 100%)" },
  { key: "analytics", label: "HR Analytics", icon: <BarChartOutlined />, color: "#7c3aed", gradient: "linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)" },
  { key: "orgchart", label: "Org Charts", icon: <BranchesOutlined />, color: "#0891b2", gradient: "linear-gradient(135deg, #0891b2 0%, #06b6d4 100%)" },
];

function ModuleTabs({ active, onChange }) {
  return (
    <div style={{
      display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center",
      background: "rgba(255, 255, 255, 0.92)",
      backdropFilter: "blur(16px)",
      WebkitBackdropFilter: "blur(16px)",
      borderRadius: 14,
      border: "1.5px solid #cbd5e1", padding: "6px 10px",
      marginBottom: 12, boxShadow: "0 4px 16px rgba(15,23,42,0.06)",
      position: "sticky", top: 12, zIndex: 100,
      transition: "all 0.25s ease",
    }}>
      {TAB_DEFS.map(t => {
        const on = active === t.key;
        return (
          <button
            key={t.key}
            onClick={() => onChange(t.key)}
            style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              padding: "8px 16px", borderRadius: 10,
              background: on ? t.gradient : "transparent",
              border: on ? "none" : "1.5px solid transparent",
              color: on ? "#ffffff" : "#475569",
              fontWeight: on ? 800 : 600,
              fontSize: 13, cursor: "pointer", outline: "none",
              transition: "all 0.22s cubic-bezier(0.4, 0, 0.2, 1)", whiteSpace: "nowrap",
              boxShadow: on ? `0 4px 14px ${t.color}38` : "none",
            }}
            onMouseEnter={e => { if (!on) { e.currentTarget.style.background = "#f1f5f9"; e.currentTarget.style.color = "#0f172a"; } }}
            onMouseLeave={e => { if (!on) { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#475569"; } }}
          >
            <span style={{ fontSize: 15, color: on ? "#ffffff" : t.color, display: "flex", alignItems: "center" }}>{t.icon}</span>
            {t.label}
          </button>
        );
      })}
    </div>
  );
}



/* ==============================================================
   EXECUTIVE DAILY HR BRIEFING MODAL
============================================================== */
function ExecutiveBriefingModal({ open, onClose, summaryData, orgName, deptName }) {
  const sd = summaryData;
  const total = sd?.total_employees ?? 0;
  const male = sd?.gender_summary?.male ?? 0;
  const female = sd?.gender_summary?.female ?? 0;
  const present = sd?.today_attendance?.present ?? 0;
  const late = sd?.today_attendance?.late ?? 0;
  const attRate = sd?.today_attendance?.attendance_rate ?? 0;
  const leave = sd?.today_leave_count ?? 0;
  const perm = sd?.today_permission_count ?? 0;
  const pLeave = sd?.pending_leave_count ?? 0;
  const pPerm = sd?.pending_permission_count ?? 0;
  const pOff = sd?.pending_offday_count ?? 0;
  const pOnDuty = sd?.pending_onduty_count ?? 0;
  const totalPending = pLeave + pPerm + pOff + pOnDuty;

  const dateStr = dayjs().format("DD MMMM YYYY");

  const copyBriefingText = () => {
    const text = `*EXECUTIVE DAILY HR BRIEFING* (${dateStr})
Org: ${orgName} | Dept: ${deptName}
----------------------------------
*Workforce Pulse:*
• Total Staff: ${total} (Male: ${male}, Female: ${female})
• Attendance Rate: ${attRate}%
• Today Present: ${present} | Late In: ${late}
• Today On Leave: ${leave} | Permission: ${perm}

*Pending Approvals Queue (${totalPending}):*
• Leave Requests: ${pLeave}
• Permission Requests: ${pPerm}
• Off Day Work Requests: ${pOff}
• On-Duty Requests: ${pOnDuty}
----------------------------------
Generated by BEMSS HR Control Center`;

    navigator.clipboard.writeText(text);
    message.success("Briefing summary copied to clipboard!");
  };

  const printBriefing = () => {
    window.print();
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={680}
      bodyStyle={{ padding: "20px 24px" }}
      title={
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingRight: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              width: 38, height: 38, borderRadius: 10,
              background: "linear-gradient(135deg, #0e497b 0%, #1d4ed8 100%)",
              color: "#ffffff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18,
            }}>
              <FileDoneOutlined />
            </div>
            <div>
              <div style={{ fontWeight: 900, fontSize: 16, color: "#0f172a" }}>Executive HR Morning Briefing</div>
              <div style={{ fontSize: 12, color: "#64748b" }}>{dateStr} · {orgName} ({deptName})</div>
            </div>
          </div>
          <Space>
            <Button icon={<CopyOutlined />} size="small" onClick={copyBriefingText} style={{ borderRadius: 6, fontWeight: 700 }}>
              Copy Text
            </Button>
            <Button icon={<PrinterOutlined />} size="small" type="primary" onClick={printBriefing} style={{ borderRadius: 6, fontWeight: 700, background: "#0e497b" }}>
              Print
            </Button>
          </Space>
        </div>
      }
    >
      <div style={{ background: "#f8fafc", borderRadius: 14, border: "1px solid #e2e8f0", padding: "16px 18px", marginBottom: 16 }}>
        <Row gutter={[12, 12]}>
          <Col span={6}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 11, color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>Total Staff</div>
              <div style={{ fontSize: 24, fontWeight: 900, color: "#0e497b" }}>{total}</div>
            </div>
          </Col>
          <Col span={6}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 11, color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>Attendance %</div>
              <div style={{ fontSize: 24, fontWeight: 900, color: "#059669" }}>{attRate}%</div>
            </div>
          </Col>
          <Col span={6}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 11, color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>Present Today</div>
              <div style={{ fontSize: 24, fontWeight: 900, color: "#15803d" }}>{present}</div>
            </div>
          </Col>
          <Col span={6}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 11, color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>Late In Today</div>
              <div style={{ fontSize: 24, fontWeight: 900, color: "#d97706" }}>{late}</div>
            </div>
          </Col>
        </Row>
      </div>

      <div style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 800, fontSize: 13, color: "#0f172a", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.05em" }}>
          ⚡ Approvals Authorization Queue ({totalPending})
        </div>
        <Row gutter={[10, 10]}>
          <Col span={12}>
            <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 10, padding: "10px 14px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontWeight: 700, color: "#1e40af", fontSize: 13 }}>Leaves Pending</span>
              <Tag color="blue" style={{ borderRadius: 8, fontWeight: 900, fontSize: 13 }}>{pLeave}</Tag>
            </div>
          </Col>
          <Col span={12}>
            <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 10, padding: "10px 14px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontWeight: 700, color: "#166534", fontSize: 13 }}>On-Duty Pending</span>
              <Tag color="green" style={{ borderRadius: 8, fontWeight: 900, fontSize: 13 }}>{pOnDuty}</Tag>
            </div>
          </Col>
          <Col span={12}>
            <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10, padding: "10px 14px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontWeight: 700, color: "#92400e", fontSize: 13 }}>Off Day Work Pending</span>
              <Tag color="warning" style={{ borderRadius: 8, fontWeight: 900, fontSize: 13 }}>{pOff}</Tag>
            </div>
          </Col>
          <Col span={12}>
            <div style={{ background: "#faf5ff", border: "1px solid #ddd6fe", borderRadius: 10, padding: "10px 14px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontWeight: 700, color: "#6b21a8", fontSize: 13 }}>Permissions Pending</span>
              <Tag color="purple" style={{ borderRadius: 8, fontWeight: 900, fontSize: 13 }}>{pPerm}</Tag>
            </div>
          </Col>
        </Row>
      </div>

      <div style={{ background: "#f1f5f9", borderRadius: 10, padding: "12px 14px", border: "1px solid #cbd5e1" }}>
        <div style={{ fontWeight: 800, fontSize: 12, color: "#334155", marginBottom: 4 }}>
          💡 Recommended Action Items for Today:
        </div>
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: "#475569" }}>
          <li>Prioritize clear-down of <strong>{pOnDuty} On-Duty</strong> and <strong>{pLeave} Leave</strong> pending approvals.</li>
          {late > 0 && <li>Review punctuality logs for <strong>{late} Late In</strong> check-ins.</li>}
          <li>Ensure department attendance compliance remains above 90%.</li>
        </ul>
      </div>
    </Modal>
  );
}

/* ==============================================================
   MAIN COMPONENT
============================================================== */
export default function HrIndex() {
  const { authData, backUrl } = useContext(AuthContext);
  const nav = useNavigation();
  const navigateTo = nav?.navigateTo;

  const [organisations, setOrganisations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState("ALL");
  const [selectedDept, setSelectedDept] = useState("ALL");
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("attendance");

  // Attendance & Briefing modal state
  const [attModal, setAttModal] = useState({ open: false, status: null, title: "", color: "" });
  const [attModalData, setAttModalData] = useState([]);
  const [attModalLoading, setAttModalLoading] = useState(false);
  const [briefingModalOpen, setBriefingModalOpen] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [o, d] = await Promise.all([
          axios.get(`${backUrl}/api/emp/organisations/`, { headers: { Authorization: `Bearer ${authData?.access}` } }),
          axios.get(`${backUrl}/api/emp/departments/`, { headers: { Authorization: `Bearer ${authData?.access}` } }),
        ]);
        setOrganisations(o.data?.results || o.data || []);
        setDepartments(d.data?.results || d.data || []);
      } catch { }
    })();
  }, [backUrl, authData?.access]);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const p = {};
      if (selectedOrg !== "ALL") p.organisation_id = selectedOrg;
      if (selectedDept !== "ALL") p.department_id = selectedDept;
      const res = await axios.get(`${backUrl}/api/emp/employees/dashboard-summary/`, {
        params: p, headers: { Authorization: `Bearer ${authData?.access}` },
      });
      setSummaryData(res.data);
    } catch { }
    finally { setLoading(false); }
  }, [selectedOrg, selectedDept, backUrl, authData?.access]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  // Open KPI detail modal for any category (Attendance, Employee, Leave, Permission)
  const openKpiModal = useCallback(async (queryParams, title, color) => {
    setAttModal({ open: true, status: queryParams.category, title, color });
    setAttModalLoading(true);
    setAttModalData([]);
    try {
      const p = { ...queryParams };
      if (selectedOrg !== "ALL") p.organisation_id = selectedOrg;
      if (selectedDept !== "ALL") p.department_id = selectedDept;
      const res = await axios.get(`${backUrl}/api/emp/employees/dashboard-kpi-detail/`, {
        params: p, headers: { Authorization: `Bearer ${authData?.access}` },
      });
      setAttModalData(res.data?.results || []);
    } catch (err) {
      console.error("Error fetching KPI detail list:", err);
    } finally {
      setAttModalLoading(false);
    }
  }, [backUrl, authData?.access, selectedOrg, selectedDept]);

  const sd = summaryData;
  const totalEmp = sd?.total_employees ?? 0;
  const maleCount = sd?.gender_summary?.male ?? 0;
  const femaleCount = sd?.gender_summary?.female ?? 0;
  const notUpdatedCount = sd?.gender_summary?.not_updated ?? sd?.gender_summary?.other ?? 0;
  const presentCount = sd?.today_attendance?.present ?? 0;
  const lateCount = sd?.today_attendance?.late ?? 0;
  const attRate = sd?.today_attendance?.attendance_rate ?? 0;
  const leaveTotal = sd?.today_leave_count ?? 0;
  const leaveFull = sd?.today_full_day_leave_count ?? 0;
  const leaveHalf = sd?.today_half_day_leave_count ?? 0;
  const permTotal = sd?.today_permission_count ?? 0;
  const permFN = sd?.today_permission_fn_count ?? 0;
  const permAN = sd?.today_permission_an_count ?? 0;

  const renderTab = () => {
    if (activeTab === "attendance") return (
      <>
        {/* 1 — Daily Attendance */}
        <Row gutter={[14, 14]} style={{ marginBottom: 16 }}>
          <Col xs={24}><EmployeeAttendance /></Col>
        </Row>

        {/* 2 — Department Benchmark */}
        <Row gutter={[14, 14]} style={{ marginBottom: 16 }}>
          <Col xs={24}><DepartmentAttendanceBenchmark /></Col>
        </Row>

        {/* 3 — Monthly Trends */}
        <Row gutter={[14, 14]} style={{ marginBottom: 16 }}>
          <Col xs={24} xl={12}><AttendanceReport /></Col>
          <Col xs={24} xl={12}><LeaveUtilization /></Col>
        </Row>

        {/* 4 — OT Payments & Advances */}
        <Row gutter={[14, 14]} style={{ marginBottom: 16 }}>
          <Col xs={24}><OtChart /></Col>
        </Row>

        {/* 5 — Loss of Pay & Salary Deductions */}
        <Row gutter={[14, 14]} style={{ marginBottom: 16 }}>
          <Col xs={24}><LopChart /></Col>
        </Row>

        {/* 6 — Workforce distribution */}
        <Row gutter={[14, 14]}>
          <Col xs={24} xl={12}><OrganisationLeave /></Col>
          <Col xs={24} xl={12}><TotalEmployees organisationId={selectedOrg} /></Col>
        </Row>
      </>
    );

    if (activeTab === "payroll") return (
      <Row gutter={[14, 14]}>
        <Col xs={24} xl={12}><PayrollSummary /></Col>
        <Col xs={24} xl={12}><DepartmentWiseSalary /></Col>
      </Row>
    );

    if (activeTab === "statutory") return (
      <Row gutter={[14, 14]}>
        <Col xs={24}><StatutoryCompliance /></Col>
      </Row>
    );

    if (activeTab === "analytics") return (
      <Row gutter={[14, 14]}>
        <Col xs={24}><HrAnalytics /></Col>
      </Row>
    );

    if (activeTab === "orgchart") return <div style={{ paddingTop: 4 }}><OrgChartList /></div>;
    return null;
  };

  return (
    <div style={{ background: "transparent", padding: "0 0 24px" }}>

      {/* ================= BRIEFING MODAL ===================== */}
      <ExecutiveBriefingModal
        open={briefingModalOpen}
        onClose={() => setBriefingModalOpen(false)}
        summaryData={summaryData}
        orgName={selectedOrg === "ALL" ? "All Organisations" : (organisations.find(o => String(o.id) === String(selectedOrg))?.name || "Organisation")}
        deptName={selectedDept === "ALL" ? "All Departments" : (departments.find(d => String(d.id) === String(selectedDept))?.name || "Department")}
      />

      {/* ================= ATTENDANCE MODAL =================== */}
      <AttendanceModal
        open={attModal.open}
        onClose={() => setAttModal(prev => ({ ...prev, open: false }))}
        title={attModal.title}
        statusColor={attModal.color}
        employees={attModalData}
        loading={attModalLoading}
      />

      {/* ================= HERO HEADER CARD ============================ */}
      <div style={{
        background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 40%, #eff6ff 100%)",
        borderRadius: 16,
        border: "1.5px solid #cbd5e1",
        marginBottom: 12,
        overflow: "hidden",
        boxShadow: "0 8px 24px -4px rgba(14, 73, 123, 0.08), 0 2px 6px -1px rgba(15, 23, 42, 0.03)",
      }}>
        {/* Top Multi-Color Accent Gradient Bar */}
        <div style={{ height: 3.5, background: "linear-gradient(90deg, #0e497b, #2563eb, #38bdf8, #8b5cf6)" }} />

        {/* Main Header Content */}
        <div style={{ padding: "14px 20px", borderBottom: "1px solid #e2e8f0" }}>
          <Row align="middle" gutter={[14, 14]}>
            <Col xs={24} md={11}>
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 13,
                  background: "linear-gradient(135deg, #0e497b 0%, #1d4ed8 100%)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: "#ffffff", fontSize: 22, flexShrink: 0,
                  boxShadow: "0 6px 16px rgba(14, 73, 123, 0.28)",
                  border: "1.5px solid rgba(255,255,255,0.3)"
                }}>
                  <TeamOutlined />
                </div>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 18, fontWeight: 900, color: "#0f172a", lineHeight: 1.2, letterSpacing: "-0.02em" }}>
                      Executive HR Control Center
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: "#64748b", marginTop: 2, fontWeight: 500 }}>
                    Real-time Workforce · Attendance · Payroll · Compliance &amp; Approvals
                  </div>
                </div>
              </div>
            </Col>
            <Col xs={24} md={13}>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", flexWrap: "wrap", alignItems: "center" }}>
                <Select value={selectedOrg} onChange={setSelectedOrg} style={{ width: 195 }} size="middle">
                  <Option value="ALL">All Organisations ({organisations.length})</Option>
                  {organisations.map(o => <Option key={o.id} value={o.id}>{o.name}</Option>)}
                </Select>
                <Select value={selectedDept} onChange={setSelectedDept} style={{ width: 185 }} size="middle">
                  <Option value="ALL">All Departments ({departments.length})</Option>
                  {departments.map(d => <Option key={d.id} value={d.id}>{d.name}</Option>)}
                </Select>
                <Button
                  type="primary"
                  icon={<ThunderboltOutlined style={{ color: "#fef08a", fontSize: 15 }} />}
                  onClick={() => setBriefingModalOpen(true)}
                  size="middle"
                  style={{
                    borderRadius: 10,
                    fontWeight: 800,
                    padding: "0 16px",
                    background: "linear-gradient(135deg, #d97706 0%, #ea580c 100%)",
                    border: "1px solid rgba(255,255,255,0.3)",
                    color: "#ffffff",
                    boxShadow: "0 4px 14px rgba(217, 119, 6, 0.38)",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 7,
                    transition: "all 0.22s cubic-bezier(0.4, 0, 0.2, 1)",
                    cursor: "pointer"
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 8px 20px rgba(217, 119, 6, 0.5)";
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "0 4px 14px rgba(217, 119, 6, 0.38)";
                  }}
                >
                  <span style={{ display: "flex", alignItems: "center", gap: 5 }}>
                    Daily Briefing
                    <span style={{
                      width: 6, height: 6, borderRadius: "50%",
                      background: "#fef08a",
                      boxShadow: "0 0 8px #fef08a, 0 0 12px #fef08a",
                      display: "inline-block", marginLeft: 2
                    }} />
                  </span>
                </Button>
                <Button
                  type="primary"
                  icon={<ReloadOutlined />}
                  onClick={fetchSummary}
                  loading={loading}
                  size="middle"
                  style={{
                    borderRadius: 9,
                    fontWeight: 800,
                    padding: "0 16px",
                    background: "linear-gradient(135deg, #0e497b 0%, #1d4ed8 100%)",
                    border: "none",
                    boxShadow: "0 4px 14px rgba(14, 73, 123, 0.25)",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6
                  }}
                >
                  Refresh
                </Button>
              </div>
            </Col>
          </Row>
        </div>

        {/* Quick Approvals Strip */}
        <div style={{
          padding: "10px 20px",
          background: "rgba(255, 255, 255, 0.85)",
          backdropFilter: "blur(12px)",
          display: "flex",
          alignItems: "center",
          gap: 10,
          flexWrap: "wrap"
        }}>
          <span style={{
            fontSize: 10.5, fontWeight: 800, color: "#475569",
            textTransform: "uppercase", letterSpacing: "0.08em",
            marginRight: 2, flexShrink: 0,
            background: "#f1f5f9", padding: "3px 8px", borderRadius: 7,
            border: "1px solid #cbd5e1"
          }}>
            ⚡ Quick Approvals:
          </span>
          <QuickBtn
            icon={<CalendarOutlined />}
            label="Leaves"
            color="#0e497b"
            count={sd?.pending_leave_count ?? 0}
            onClick={() => navigateTo && navigateTo("empleaves")}
          />
          <QuickBtn
            icon={<FileProtectOutlined />}
            label="Permissions"
            color="#2563eb"
            count={sd?.pending_permission_count ?? 0}
            onClick={() => navigateTo && navigateTo("emppermissions")}
          />
          <QuickBtn
            icon={<ScheduleOutlined />}
            label="Off Day Work"
            color="#d97706"
            count={sd?.pending_offday_count ?? 0}
            onClick={() => navigateTo && navigateTo("empcompoffs")}
          />
          <QuickBtn
            icon={<ClockCircleOutlined />}
            label="On-Duty"
            color="#059669"
            count={sd?.pending_onduty_count ?? 0}
            onClick={() => navigateTo && navigateTo("onduty")}
          />
        </div>
      </div>



      {/* ================= KPI CARDS ========================= */}
      <Row gutter={[14, 14]} style={{ marginBottom: 14 }}>

        {/* Card 1 — Total Active Staff */}
        <Col xs={24} sm={12} xl={6}>
          <KpiCard
            accent="#0e497b"
            icon={<UsergroupAddOutlined />}
            label="Total Active Staff"
            bigValue={totalEmp}
            extraTag={notUpdatedCount > 0 ? `${notUpdatedCount} Not Updated` : undefined}
            onExtraTagClick={() => openKpiModal({ category: "EMPLOYEE", gender: "NOT_UPDATED" }, `Unupdated Gender Staff (${notUpdatedCount})`, "#64748b")}
            loading={loading}
            onCardClick={() => openKpiModal({ category: "EMPLOYEE", gender: "ALL" }, `All Active Staff (${totalEmp})`, "#0e497b")}
            s1Val={maleCount} s1Label="Male" s1Color="#2563eb" s1Icon={<ManOutlined style={{ fontSize: 14 }} />}
            onS1Click={() => openKpiModal({ category: "EMPLOYEE", gender: "Male" }, `Active Male Staff (${maleCount})`, "#2563eb")}
            s2Val={femaleCount} s2Label="Female" s2Color="#ec4899" s2Icon={<WomanOutlined style={{ fontSize: 14 }} />}
            onS2Click={() => openKpiModal({ category: "EMPLOYEE", gender: "Female" }, `Active Female Staff (${femaleCount})`, "#ec4899")}
          />
        </Col>

        {/* Card 2 — Today's Attendance (clickable sub-stats) */}
        <Col xs={24} sm={12} xl={6}>
          <KpiCard
            accent="#059669"
            icon={<CheckCircleOutlined />}
            label="Today's Attendance"
            bigValue={presentCount + lateCount > 0 ? `${attRate}%` : "-"}
            loading={loading}
            onCardClick={() => openKpiModal({ category: "ATTENDANCE", status: "ALL_ATTENDANCE" }, `Today's Attendance List (${presentCount + lateCount}) - ${dayjs().format("DD MMM YYYY")}`, "#059669")}
            s1Val={presentCount}
            s1Label="Present"
            s1Color="#15803d"
            s1Icon={<CheckOutlined style={{ fontSize: 13 }} />}
            onS1Click={() => openKpiModal({ category: "ATTENDANCE", status: "PRESENT" }, `Present Employees Today (${presentCount}) - ${dayjs().format("DD MMM YYYY")}`, "#15803d")}
            s2Val={lateCount}
            s2Label="Late In"
            s2Color="#b45309"
            s2Icon={<ClockCircleOutlined style={{ fontSize: 13 }} />}
            onS2Click={() => openKpiModal({ category: "ATTENDANCE", status: "LATE" }, `Late In Employees Today (${lateCount}) - ${dayjs().format("DD MMM YYYY")}`, "#b45309")}
          />
        </Col>

        {/* Card 3 — On Leave Today */}
        <Col xs={24} sm={12} xl={6}>
          <KpiCard
            accent="#dc2626"
            icon={<CalendarOutlined />}
            label="On Leave Today"
            bigValue={leaveTotal}
            loading={loading}
            onCardClick={() => openKpiModal({ category: "LEAVE", duration_type: "ALL" }, `Employees On Leave Today - ${dayjs().format("DD MMM YYYY")}`, "#dc2626")}
            s1Val={leaveFull} s1Label="Full Day" s1Color="#991b1b" s1Icon={<FileTextOutlined style={{ fontSize: 13 }} />}
            onS1Click={() => openKpiModal({ category: "LEAVE", duration_type: "Full Day" }, `Full Day Leave Today - ${dayjs().format("DD MMM YYYY")}`, "#991b1b")}
            s2Val={leaveHalf} s2Label="Half Day" s2Color="#d97706" s2Icon={<CalendarOutlined style={{ fontSize: 13 }} />}
            onS2Click={() => openKpiModal({ category: "LEAVE", duration_type: "Half Day" }, `Half Day Leave Today - ${dayjs().format("DD MMM YYYY")}`, "#d97706")}
          />
        </Col>

        {/* Card 4 — Permissions Today */}
        <Col xs={24} sm={12} xl={6}>
          <KpiCard
            accent="#7c3aed"
            icon={<FileProtectOutlined />}
            label="Permissions Today"
            bigValue={permTotal}
            loading={loading}
            onCardClick={() => openKpiModal({ category: "PERMISSION", session: "ALL" }, `Employees On Permission Today - ${dayjs().format("DD MMM YYYY")}`, "#7c3aed")}
            s1Val={permFN} s1Label="Morning" s1Color="#2563eb" s1Icon={<FieldTimeOutlined style={{ fontSize: 13 }} />}
            onS1Click={() => openKpiModal({ category: "PERMISSION", session: "FN" }, `Morning (FN) Permission Today - ${dayjs().format("DD MMM YYYY")}`, "#2563eb")}
            s2Val={permAN} s2Label="Afternoon" s2Color="#d97706" s2Icon={<FieldTimeOutlined style={{ fontSize: 13 }} />}
            onS2Click={() => openKpiModal({ category: "PERMISSION", session: "AN" }, `Afternoon (AN) Permission Today - ${dayjs().format("DD MMM YYYY")}`, "#d97706")}
          />
        </Col>
      </Row>

      {/* ================= MODULE TABS ======================= */}
      <ModuleTabs active={activeTab} onChange={setActiveTab} />

      {/* ================= TAB CONTENT ======================= */}
      <div style={{
        background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
        border: "1.5px solid #cbd5e1",
        borderRadius: 16, padding: "16px 18px 22px",
        boxShadow: "0 8px 24px -4px rgba(15, 23, 42, 0.05), 0 2px 6px -1px rgba(15, 23, 42, 0.03)",
        minHeight: 220,
      }}>
        {renderTab()}
      </div>
    </div>
  );
}
